<template>
  <CheckboxInput
    v-model="store.state.WordleScores.onlyKnownUsers"
    class="my-4 font-thin"
    :label="t('pages.wordle.only_known_users_checkbox')"
  ></CheckboxInput>
  <div class="flex items-center justify-between">
    <chevron-left-icon
      class="w-6 cursor-pointer text-gray-600"
      @click="decreaseScoreDate"
    ></chevron-left-icon>
    <h1
      class="text-center text-lg font-medium text-kekkle-dark dark:text-white"
    >
      {{ t("pages.wordle.statistics_off") }} <br />
      <span class="font-bold text-kekkle-dark dark:text-white">
        {{ scoreDate.toLocaleDateString() }}</span
      >
    </h1>
    <chevron-right-icon
      class="w-6 cursor-pointer text-gray-600"
      @click="increaseScoreDate"
    ></chevron-right-icon>
  </div>

  <spinner
    v-if="(fetchingScores || fetchingGameId) && !scores.length"
    class="mx-auto mt-16 w-8 animate-spin text-black-dark dark:text-white"
  ></spinner>

  <div
    v-else
    class="w-full rounded border-2 border-black-dark bg-white p-4 text-kekkle-dark shadow-teal-hard-sm dark:bg-kekkle-dark-light dark:text-white"
  >
    <div>
      <div
        v-for="(score, index) in scores"
        :key="score.id"
        class="relative mb-2 flex flex-nowrap items-center gap-2 p-2"
        :class="{
          'border-b border-black-dark':
            scores[index + 1]?.score !== score.data.score,
        }"
      >
        <div class="= flex grow flex-wrap items-center gap-6">
          <p class="text-3xl font-bold">
            <span
              v-if="store.getters[GET_AUTH_UUID] === score.data.user_id"
              class="pointer text-xl"
              >👉</span
            >
            <span v-if="score.position === 1"> 🥇 </span>
            <span v-else-if="score.position === 2"> 🥈 </span>
            <span v-else-if="score.position === 3"> 🥉 </span>
            <span v-else> {{ score.position }}</span>
          </p>
          <div>
            <p class="">
              {{ t("pages.wordle.name") }}
              <span class="font-bold">{{ score.data.username }}</span>
            </p>
            <p v-if="groupMembers[score.user_id]" class="">
              {{ t("pages.wordle.nick_name") }}
              <span class="font-bold">{{
                groupMembers[score.data.user_id].name
              }}</span>
            </p>
            <p class="">
              {{ t("pages.wordle.score") }}
              <span class="font-bold">{{ score.data.new_score }}</span>
            </p>
          </div>
        </div>
        <mini-wordle-board
          :hide-letters="!hasPlayedTodaysGame && lookingAtNewestGameScore"
          :game-state="score.data.game_state"
        />
      </div>
      <p v-if="!scores.length">{{ t("pages.wordle.no_scores_known") }}</p>
    </div>
  </div>
</template>
<script setup>
import MiniWordleBoard from "@/views/games/wordle/components/MiniWordleBoard.vue";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/outline";
import { useStore } from "vuex";
import { computed, watch } from "vue";
import Spinner from "@/components/Spinner.vue";
import { GET_AUTH_UUID } from "@/store/modules/auth";
import { GET_GROUP_MEMBERS } from "@/store/modules/groups";
import { useI18n } from "vue-i18n";
import CheckboxInput from "@/components/Inputs/CheckboxInput.vue";
const { t } = useI18n({ useScope: "global" });

const store = useStore();

if (store.getters.getMatchDate && store.getters.getMatchDate.toDate()) {
  store.commit("setScoreDate", store.getters.getMatchDate.toDate());
}
store.dispatch("fetchScores", store.getters.getScoreDate);

const changeScoreDate = (days) => {
  let date = new Date(store.getters.getScoreDate);
  date.setDate(date.getDate() + days);
  store.commit("setScoreDate", date);
};

function increaseScoreDate() {
  changeScoreDate(1);
}

function decreaseScoreDate() {
  changeScoreDate(-1);
}

watch(
  () => store.state.WordleScores.onlyKnownUsers,
  (newValue) => {
    store.dispatch("fetchScores", store.getters.getScoreDate);
    store.commit("setOnlyKnownUsersWorldeLeaderboard", newValue);
    store.commit("setOnlyKnownUsersWordleScores", newValue);
    store.commit("setOnlyKnownUsersWordleMonthlyScores", newValue);
  },
);

watch(
  () => store.getters.getScoreDate,
  () => {
    store.dispatch("fetchScores", store.getters.getScoreDate);
  },
);

const scores = computed(() => {
  let scores = store.getters.getScores;
  let position = 0;
  let lastScore = null;

  if (!scores[0]?.data?.score) {
    return [];
  }
  scores.sort((a, b) => b.data.new_score - a.data.new_score);

  return scores.map((score) => {
    if (score.data.new_score !== lastScore) {
      position = position + 1;
      lastScore = score.data.new_score;
    }

    return {
      data: score.data,
      position: position,
    };
  });
});
const fetchingScores = computed(() => store.getters.getFetchingScores);
const fetchingGameId = computed(() => store.getters.getFetchingGameId);
const groupMembers = computed(() => store.getters[GET_GROUP_MEMBERS]);
const scoreDate = computed(() => store.getters.getScoreDate);

const hasPlayedTodaysGame = computed(() => store.getters.getHasFinished);
const lookingAtNewestGameScore = computed(() => {
  return (
    store.getters.getMatchId === store.getters.getCurrentGameId ||
    (store.getters.getMatchDate &&
      store.getters.getMatchDate.toDate() < store.getters.getScoreDate)
  );
});
</script>
<style lang="scss">
.pointer {
  font-size: 28px;
}
</style>
